import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const StickyHeader = ({
  children,
  className = '',
  disabled = false,
  headerSelector = '',
  offsetTop = 0,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const placeholderRef = useRef(null);
  const headerHeight = useRef(0);
  const offsetTopRef = useRef(offsetTop);

  const unstickHeader = () => {
    if (!isSticky) return;

    const header = headerRef.current;
    const placeholder = placeholderRef.current;

    setIsSticky(false);

    if (placeholder) {
      placeholder.style.height = '0';
      placeholder.style.display = 'none';
    }

    if (header) {
      header.style.position = 'relative';
      header.style.top = 'auto';
      header.style.left = 'auto';
      header.style.width = '100%';
      header.style.zIndex = 'auto';
      header.style.maxWidth = '100%';
      header.classList.remove('is-stuck');
    }
  };

  const stickHeader = (containerRect) => {
    if (isSticky) return;

    const header = headerRef.current;
    const placeholder = placeholderRef.current;

    setIsSticky(true);

    if (placeholder) {
      placeholder.style.height = headerHeight.current + 'px';
      placeholder.style.display = 'block';
    }

    if (header) {
      const formWidth = containerRect.width;
      const formLeft = containerRect.left;

      header.style.position = 'fixed';
      header.style.top = offsetTopRef.current + 'px';
      header.style.left = formLeft + 'px';
      header.style.width = formWidth + 'px';
      header.style.zIndex = '123';
      header.style.maxWidth = '100%';
      header.classList.add('is-stuck');
    }
  };

  const handleScroll = () => {
    if (disabled || !headerRef.current || !containerRef.current) return;

    const header = headerRef.current;
    const container = containerRef.current;
    const placeholder = placeholderRef.current;
    const containerRect = container.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const isFormVisible = containerRect.bottom > 0;

    if (!isFormVisible) {
      if (isSticky) {
        unstickHeader();
      }
      return;
    }

    const shouldStick =
      containerRect.top <= offsetTopRef.current &&
      containerRect.bottom > headerHeight.current + offsetTopRef.current;

    if (shouldStick && !isSticky) {
      stickHeader(containerRect);
    } else if (!shouldStick && isSticky) {
      unstickHeader();
    }
  };

  const initHeader = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const header = container.querySelector(headerSelector);
    if (!header) return;

    headerRef.current = header;
    headerHeight.current = header.offsetHeight;

    let placeholder = container.querySelector('.sticky-header-placeholder');
    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.className = 'sticky-header-placeholder';
      placeholder.style.height = '0';
      placeholder.style.display = 'none';
      placeholder.style.transition = 'height 0.3s ease';
      header.parentNode.insertBefore(placeholder, header);
    }
    placeholderRef.current = placeholder;

    header.style.position = 'relative';
    header.style.top = 'auto';
    header.style.left = 'auto';
    header.style.width = '100%';
    header.style.zIndex = 'auto';
  }, [headerSelector]);

  useEffect(() => {
    try {
      initHeader();
    } catch {
      setError(true);
    }
  }, [initHeader]);

  useEffect(() => {
    if (!headerRef.current) return;

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    const timeoutId = setTimeout(handleScroll, 50);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      clearTimeout(timeoutId);
      unstickHeader();
    };
  }, [handleScroll, unstickHeader]);

  useEffect(() => {
    if (disabled) {
      unstickHeader();
    } else {
      handleScroll();
    }
  }, [disabled, handleScroll, unstickHeader]);

  useEffect(() => {
    initHeader();
  }, [children, initHeader]);

  if (error) {
    return children;
  }

  return (
    <div ref={containerRef} className={className} style={{ position: 'relative' }}>
      {children}
    </div>
  );
};

StickyHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  headerSelector: PropTypes.string,
  offsetTop: PropTypes.number,
};

export default StickyHeader;
